import React from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import { Alert, BaseCard } from "@platform/design-system";
import {
  useRfpCoverages,
  useApi,
  mapCoverages,
  type RfpCoveragesResponse,
} from "@platform/api";
import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from "@tanstack/react-query";
import { useParams } from "react-router";
import { useSignedInUser } from "@platform/user/client";

import { useAnalyticsEvents } from "../../../hooks/useAnalyticsEvents";
import envVariables from "../../../projectEnvVariables";
import { sortProductsByCoverage } from "../../RFP/utils";

import { CoveragesCard, CoveragesCardData } from "./coveragesCard";
import GenerateRFPButton from "./generateRfpButton";

const onOpenSharePoint = (url: string): void => {
  window.open(url, "_blank");
};

const WorkbenchBuild = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const { trackRfpTemplateGeneration } = useAnalyticsEvents();
  const { isBrokerOrAgent } = useSignedInUser();
  const axiosInstance = useApi();

  const query = useQuery<BuildResponse>({
    queryKey: ["rfp", "workbench", "build", id],
    queryFn: () =>
      axiosInstance
        .get<BuildResponse>(`brokerportal/v1/rfp/${id}/workbench/build`)
        .then((res) => res.data),
  });

  const coveragesQuery = useRfpCoverages<CoveragesCardData>({
    rfpId: id ?? "",
    enabled: !!id,
    select: selectCoverages,
  });

  const mutation = useMutation<RFPDocumentResponse, Error, Payload>({
    mutationFn: (payload) =>
      axiosInstance
        .post<RFPDocumentResponse>(
          `${envVariables.VITE_REACT_APP_PORTAL_API_URL}/v3/api/rfp-documents`,
          payload,
        )
        .then((res) => res.data),
    onSuccess: (data) => {
      onOpenSharePoint(data.fullPath);
      query.refetch();
      trackRfpTemplateGeneration();
    },
  });

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      {isBrokerOrAgent ? null : (
        <BaseCard
          title="RFP Template"
          action={<GenerateRFPAction query={query} mutation={mutation} />}
        >
          {mutation.isError ? (
            <Alert
              severity="error"
              action={
                // TODO(ds-button): link-button — DS Button type isn't polymorphic (href/target/rel)
                <Button
                  variant="text"
                  target="_blank"
                  rel="noreferrer noopener"
                  href="mailto:help@centrobenefits.com"
                >
                  CONTACT SUPPORT
                </Button>
              }
            >
              The RFP Template was unable to generate
            </Alert>
          ) : null}
        </BaseCard>
      )}
      <CoveragesCard
        status={coveragesQuery.status}
        data={coveragesQuery.data}
        error={coveragesQuery.error}
        retry={() => coveragesQuery.refetch()}
      />
    </Box>
  );
};

function GenerateRFPAction({
  query,
  mutation,
}: {
  query: UseQueryResult<BuildResponse, Error>;
  mutation: UseMutationResult<RFPDocumentResponse, Error, Payload>;
}): React.JSX.Element {
  const onGenerate = (): void => {
    if (!query.data) {
      return;
    }

    mutation.mutate({
      rfpId: query.data.ecoRfpId,
      documentSharepointLocation: query.data.analystDocsFolderUrl,
      documentType: "RfpTemplate",
      platform: "rfpWorkbench",
    });
  };

  if (query.isLoading) {
    return (
      <Box display="flex" justifyContent="center" mr={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (query.isError) {
    return <Alert severity="error">Error downloading template url.</Alert>;
  }

  return (
    <GenerateRFPButton
      status={query.data.templateGeneratedUrl ? "success" : mutation.status}
      error={mutation.error}
      onGenerateClick={onGenerate}
      onOpenSharePointClick={() =>
        onOpenSharePoint(
          query.data.templateGeneratedUrl ?? mutation.data.fullPath,
        )
      }
    />
  );
}

type RFPDocumentResponse = {
  marketSolutionsRequestId: string;
  fileName: string;
  isExcelTemplateCreated: boolean;
  message: string | null;
  fullPath: string;
  environment: string | null;
};

type Payload = {
  rfpId: string;
  documentSharepointLocation: string;
  documentType: string;
  platform: string;
};

type BuildResponse = {
  ecoRfpId: string | null;
  analystDocsFolderUrl: string | null;
  templateGeneratedUrl: string | null;
};

function selectCoverages(resp: RfpCoveragesResponse): CoveragesCardData {
  return {
    currentCoverages: mapCoverages(
      sortProductsByCoverage(resp.currentCoverages, "name"),
    ),
    newCoverages: mapCoverages(
      sortProductsByCoverage(resp.newCoverages, "name"),
    ),
  };
}

export default WorkbenchBuild;
